import { domToPng } from "modern-screenshot";
import Highcharts from "highcharts";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsReact from "highcharts-react-official";
import * as L from "leaflet";
import { getMapInstance } from "../../../utils/mapRegistry";
import { PDF_CONSTANTS } from "../constants/reportConstants";
import { compressImage } from "./imageUtils";
import {
  createMapFilter,
  hideUIElements,
  waitForMapTilesActualLoad,
  getMapContainer,
  validateMapContainer,
} from "./mapFilters";
import { ErrorLogger, ImageCaptureError } from "./errorHandling";
import {
  waitForLegendReadiness,
  waitForImagesLoaded,
  waitForPaintCycle,
} from "./legendReadiness";
import { validateCapture } from "./captureValidation";

type CaptureLayer = L.Layer & {
  _path?: SVGElement;
  _icon?: HTMLElement;
  _shadow?: HTMLElement;
  _edgeMeasureLabels?: CaptureLayer[];
};

type BoundsAwareLayer = L.Layer & {
  getBounds?: () => L.LatLngBounds;
  getLatLngs?: () => L.LatLng[] | L.LatLng[][] | L.LatLng[][][];
};

// Exact mainland PE bounds (IBGE — excludes Fernando de Noronha)
const PE_MAINLAND_BOUNDS: [[number, number], [number, number]] = [
  [-9.48, -41.34],
  [-7.16, -34.79],
];
// Fernando de Noronha archipelago, ~545 km off the PE coast — outside
// PE_MAINLAND_BOUNDS, so a report with a Noronha-only raster active needs to
// fit here instead, or the layer never appears in the captured image.
const FERNANDO_NORONHA_BOUNDS: [[number, number], [number, number]] = [
  [-3.905, -32.46],
  [-3.79, -32.36],
];
// Union of the two boxes above. Used when active layers span both areas, so
// neither the mainland layers nor the Noronha layer get cropped out of frame.
const MAINLAND_AND_NORONHA_BOUNDS: [[number, number], [number, number]] = [
  [PE_MAINLAND_BOUNDS[0][0], PE_MAINLAND_BOUNDS[0][1]],
  [FERNANDO_NORONHA_BOUNDS[1][0], FERNANDO_NORONHA_BOUNDS[1][1]],
];

export type ReportMapFocus = "mainland" | "noronha" | "combined";
// Padding (px) around PE mainland bounds when fitting the map — keeps the whole
// state (including the Recife coastal tip) inside the captured image with a margin
const PE_FIT_BOUNDS_PADDING_PX = 24;
const PE_MAP_SETTLE_MS = 300;
// Extra wait for WFS layers: moveend triggers re-fetch, but XHR + React re-render need time
const PE_WFS_LAYER_LOAD_MS = 4000;

function resolveLeafletMap(): L.Map | null {
  const fromRegistry = getMapInstance();
  if (fromRegistry) return fromRegistry;

  const container = getMapContainer();
  if (container) {
    const leafletId = (container as any)._leaflet_id;
    if (leafletId && (L as any).Map?._maps) {
      const map = (L as any).Map._maps[leafletId] as L.Map;
      if (map) return map;
    }
  }

  return null;
}

export async function captureMapFittedToPernambuco(
  onProgress?: ProgressCallback,
  mapFocus: ReportMapFocus = "mainland"
): Promise<string | null> {
  const map = resolveLeafletMap();
  if (!map) return captureMap("main-map", onProgress, false);

  const savedCenter = map.getCenter();
  const savedZoom = map.getZoom();
  const savedZoomSnap = (map.options as any).zoomSnap;

  try {
    const targetBounds =
      mapFocus === "noronha"
        ? FERNANDO_NORONHA_BOUNDS
        : mapFocus === "combined"
        ? MAINLAND_AND_NORONHA_BOUNDS
        : PE_MAINLAND_BOUNDS;
    const peBounds = L.latLngBounds(targetBounds[0], targetBounds[1]);

    (map.options as any).zoomSnap = 0;
    // fitBounds (instead of a manually computed zoom) guarantees the entire PE
    // mainland — including the easternmost coastal tip near Recife — stays inside the view
    map.fitBounds(peBounds, {
      padding: [PE_FIT_BOUNDS_PADDING_PX, PE_FIT_BOUNDS_PADDING_PX],
      animate: false,
    });

    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
    await new Promise((resolve) => setTimeout(resolve, PE_MAP_SETTLE_MS));

    // Wait for base tiles and WFS vector layers in parallel.
    // WFS hooks re-fetch on moveend, but XHR + React re-render take additional time.
    await Promise.all([
      waitForMapTilesActualLoad(PDF_CONSTANTS.MAP_TILES_WAIT),
      new Promise((resolve) => setTimeout(resolve, PE_WFS_LAYER_LOAD_MS)),
    ]);

    return await captureMap("main-map", onProgress, false);
  } finally {
    (map.options as any).zoomSnap = savedZoomSnap;
    map.setView(savedCenter, savedZoom, { animate: false });
  }
}

if (typeof Highcharts === "object") {
  HighchartsExporting(Highcharts);
  HighchartsOfflineExporting(Highcharts);
}

/**
 * Progress callback type for reporting capture progress
 */
export type ProgressCallback = (progress: {
  step: string;
  message: string;
  percentage: number;
}) => void;

/**
 * Metadata for the PDF report
 */
export interface PDFMetadata {
  projectName: string;
  description: string;
  timestamp: string;
  location?: {
    lat: number;
    lng: number;
    address?: string;
  };
}

/**
 * Images captured for the PDF
 */
export interface PDFImages {
  map?: string; // base64 PNG/JPEG
  charts: string[]; // base64 PNGs from Highcharts
  legends: string[]; // base64 PNGs or URLs
}

/**
 * Legend item for rendering in PDF
 */
export interface LegendItem {
  layerName: string;
  category: string;
  items: Array<{
    label: string;
    style: {
      type: "icon" | "line" | "polygon";
      color?: string;
      fillColor?: string;
      fillOpacity?: number;
      weight?: number;
      dashArray?: string;
      icon?: string;
    };
  }>;
}

/**
 * Data to include in the PDF
 */
export interface PDFData {
  statistics?: {
    globalIrradiation?: string;
    diffuseIrradiation?: string;
    directIrradiation?: string;
  };
  monthlyData?: number[];
  distributionData?: Array<{
    min: number;
    max: number;
    percentage: number;
  }>;
  areaStats?: {
    min: number;
    max: number;
    mean: number;
    std_dev: number;
  };
  activeLayers: Array<{
    name: string;
    category: string;
  }>;
  legends?: LegendItem[];
}

/**
 * Complete payload for PDF generation
 */
export interface PDFPayload {
  metadata: PDFMetadata;
  images: PDFImages;
  data: PDFData;
}

/**
 * Hides a single Leaflet layer's DOM element and returns a restore function.
 * Uses visibility (not display) so Leaflet's internal state stays intact.
 */
function hideLayerDOM(layer: CaptureLayer): (() => void) | null {
  const l = layer;
  const restores: Array<() => void> = [];

  if (l._path instanceof SVGElement) {
    const path = l._path;
    const prev = path.style.visibility;
    path.style.visibility = "hidden";
    restores.push(() => {
      path.style.visibility = prev;
    });
  }
  if (l._icon instanceof HTMLElement) {
    const icon = l._icon;
    const prev = icon.style.visibility;
    icon.style.visibility = "hidden";
    restores.push(() => {
      icon.style.visibility = prev;
    });
  }
  if (l._shadow instanceof HTMLElement) {
    const shadow = l._shadow;
    const prev = shadow.style.visibility;
    shadow.style.visibility = "hidden";
    restores.push(() => {
      shadow.style.visibility = prev;
    });
  }
  if (Array.isArray(l._edgeMeasureLabels)) {
    l._edgeMeasureLabels.forEach((label) => {
      const restore = hideLayerDOM(label);
      if (restore) restores.push(restore);
    });
  }

  if (restores.length === 0) return null;
  return () => restores.forEach((fn) => fn());
}

/**
 * Captures the map zoomed in on a specific drawing, hiding all others.
 * Restores the original view and layer visibility after capture.
 */
export async function captureMapFocusedOnDrawing(
  selectedLayer: unknown,
  otherLayers: unknown[],
  onProgress?: ProgressCallback,
  extraBoundsLayers?: unknown[]
): Promise<string | null> {
  const l = selectedLayer as any;
  // Synthetic layers (e.g. a município's geometry, never `addTo(map)`'d) have
  // no `_map` — fall back to the registered map instance so bounds-fitting
  // still works from the detached layer's geometry.
  const map = l._map ?? resolveLeafletMap();

  if (!map) {
    return captureMap("main-map", onProgress, false);
  }

  const savedCenter = map.getCenter();
  const savedZoom = map.getZoom();

  // Hide other drawings (DOM only, no Leaflet state change)
  const restoreFunctions: Array<() => void> = [];
  for (const other of otherLayers) {
    const restore = hideLayerDOM(other as CaptureLayer);
    if (restore) restoreFunctions.push(restore);
  }

  try {
    // Build combined bounds: selected area + extra layers (e.g. distance rulers)
    let combinedBounds: L.LatLngBounds | null = null;

    if (typeof l.getBounds === "function") {
      combinedBounds = l.getBounds() as L.LatLngBounds;
    } else if (typeof l.getLatLng === "function") {
      combinedBounds = L.latLngBounds([l.getLatLng()]);
    }

    for (const extra of extraBoundsLayers ?? []) {
      const el = extra as BoundsAwareLayer;
      if (typeof el.getBounds === "function") {
        combinedBounds = combinedBounds
          ? combinedBounds.extend(el.getBounds())
          : el.getBounds();
        continue;
      }
      if (typeof el.getLatLngs !== "function") continue;

      const latlngs = el.getLatLngs().flat(2) as L.LatLng[];
      for (const ll of latlngs) {
        combinedBounds = combinedBounds ? combinedBounds.extend(ll) : L.latLngBounds([ll]);
      }
    }

    if (combinedBounds) {
      map.fitBounds(combinedBounds, { padding: [40, 40], animate: false });

      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
      await new Promise((resolve) => setTimeout(resolve, PE_MAP_SETTLE_MS));

      // Bounds now span beyond the drawing (distance rulers), so the map moves
      // significantly — WFS layers need the same re-fetch wait as a full reframe.
      await Promise.all([
        waitForMapTilesActualLoad(PDF_CONSTANTS.MAP_TILES_WAIT),
        new Promise((resolve) => setTimeout(resolve, PE_WFS_LAYER_LOAD_MS)),
      ]);
    } else {
      await new Promise((resolve) => setTimeout(resolve, 150));
      await waitForMapTilesActualLoad(PDF_CONSTANTS.MAP_TILES_WAIT);
    }

    return await captureMap("main-map", onProgress, false);
  } finally {
    restoreFunctions.forEach((fn) => fn());
    map.setView(savedCenter, savedZoom, { animate: false });
  }
}

/**
 * Capture the map element as a PNG image with UI elements hidden
 * @param elementId - ID of the map container element
 * @param onProgress - Optional progress callback
 * @param includeLegend - Whether to include the statistics panel/legend in the capture
 * @returns Base64 encoded PNG image
 */
export async function captureMap(
  elementId: string = "main-map",
  onProgress?: ProgressCallback,
  includeLegend: boolean = false
): Promise<string | null> {
  try {
    onProgress?.({
      step: "capturing-map",
      message: includeLegend
        ? "Capturando mapa com legendas..."
        : "Capturando mapa...",
      percentage: 20,
    });

    // Get the target element
    let targetElement: HTMLElement | null;

    if (includeLegend) {
      targetElement = document.querySelector(".leaflet-container")
        ?.parentElement as HTMLElement;
      if (!targetElement) {
        ErrorLogger.logWarning(
          "Parent container not found, falling back to map element"
        );
        targetElement = document.getElementById(elementId);
      }
    } else {
      targetElement = getMapContainer() || document.getElementById(elementId);
    }

    if (!targetElement) {
      throw new ImageCaptureError("Map element not found", "map");
    }

    // Validate the map container
    validateMapContainer(targetElement);

    // Wait for map tiles to actually load (with detection)
    await waitForMapTilesActualLoad(PDF_CONSTANTS.MAP_TILES_WAIT);

    // Find the Leaflet container
    const leafletContainer = targetElement.querySelector(
      ".leaflet-container"
    ) as HTMLElement;
    if (!leafletContainer) {
      throw new ImageCaptureError("Leaflet container not found", "map");
    }

    // Hide all Leaflet controls and app UI in the real DOM before capture,
    // then restore them regardless of success or failure.
    const restoreUI = hideUIElements();
    let dataUrl: string;
    try {
      dataUrl = await domToPng(leafletContainer, {
        scale: 1.5,
        backgroundColor: "#ffffff",
        filter: createMapFilter(),
      });
    } finally {
      restoreUI();
    }

    ErrorLogger.logInfo(
      `Map captured, size: ${(dataUrl.length / 1024).toFixed(2)} KB`
    );

    // Compress the image
    onProgress?.({
      step: "compressing-map",
      message: "Comprimindo imagem do mapa...",
      percentage: 30,
    });

    try {
      const compressed = await compressImage(
        dataUrl,
        PDF_CONSTANTS.COMPRESSION_QUALITY,
        PDF_CONSTANTS.COMPRESSION_MAX_WIDTH,
        PDF_CONSTANTS.COMPRESSION_MAX_HEIGHT
      );
      ErrorLogger.logInfo(
        `Map compressed, size: ${(compressed.length / 1024).toFixed(2)} KB`
      );
      return compressed;
    } catch (compressionError) {
      ErrorLogger.logWarning("Image compression failed, using original");
      return dataUrl;
    }
  } catch (error) {
    ErrorLogger.logError(
      error instanceof Error ? error : new Error(String(error)),
      "captureMap"
    );
    throw new ImageCaptureError("Falha ao capturar o mapa", "map");
  }
}

/**
 * Capture a Highcharts chart as a PNG image using Highcharts exporting module
 * @param chartRef - Reference to the Highcharts React component
 * @returns Base64 encoded PNG image
 */
export async function captureChart(
  chartRef: HighchartsReact.RefObject | null,
  title?: string
): Promise<string | null> {
  try {
    if (!chartRef || !chartRef.chart) {
      ErrorLogger.logWarning("Chart reference is not available");
      return null;
    }

    const chart = chartRef.chart;

    return new Promise<string | null>((resolve) => {
      try {
        // Get high-quality SVG from Highcharts
        const svg = chart.getSVG({
          chart: {
            backgroundColor: "#ffffff",
          },
          ...(title
            ? {
                title: {
                  text: title,
                  align: "left",
                  style: {
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#333",
                  },
                },
              }
            : {}),
        });

        // Convert SVG to PNG using canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          ErrorLogger.logError(
            new Error("Failed to get canvas context"),
            "captureChart"
          );
          resolve(null);
          return;
        }

        // Create image from SVG
        const img = new Image();
        const svgBlob = new Blob([svg], {
          type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          // Set canvas size for high resolution
          const scale = PDF_CONSTANTS.CHART_CAPTURE_SCALE;
          canvas.width = chart.chartWidth * scale;
          canvas.height = chart.chartHeight * scale;

          // Fill white background
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Draw chart image scaled
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0, chart.chartWidth, chart.chartHeight);

          // Convert to PNG data URL
          const dataUrl = canvas.toDataURL("image/png", 1.0);

          ErrorLogger.logInfo(
            `Chart captured, size: ${(dataUrl.length / 1024).toFixed(2)} KB`
          );

          // Clean up
          URL.revokeObjectURL(url);

          resolve(dataUrl);
        };

        img.onerror = (err) => {
          ErrorLogger.logError(
            new Error("Failed to load chart SVG image"),
            "captureChart"
          );
          URL.revokeObjectURL(url);
          resolve(null);
        };

        img.src = url;
      } catch (exportError) {
        ErrorLogger.logError(
          exportError instanceof Error
            ? exportError
            : new Error(String(exportError)),
          "captureChart"
        );
        resolve(null);
      }
    });
  } catch (error) {
    ErrorLogger.logError(
      error instanceof Error ? error : new Error(String(error)),
      "captureChart"
    );
    return null;
  }
}

/**
 * Capture all charts from an array of chart references
 * @param chartRefs - Array of chart references
 * @param onProgress - Optional progress callback
 * @returns Array of base64 encoded PNG images
 */
export async function captureCharts(
  chartRefs: Array<HighchartsReact.RefObject | null>,
  onProgress?: ProgressCallback
): Promise<string[]> {
  const charts: string[] = [];

  onProgress?.({
    step: "capturing-charts",
    message: "Capturando gráficos...",
    percentage: 40,
  });

  for (let i = 0; i < chartRefs.length; i++) {
    const chartRef = chartRefs[i];
    if (chartRef) {
      const chartImage = await captureChart(chartRef);
      if (chartImage) {
        charts.push(chartImage);
      }
    }
  }

  ErrorLogger.logInfo(`Captured ${charts.length} charts`);

  return charts;
}

/**
 * Capture the legends panel as a PNG image
 * Uses the hidden legends-panel-for-capture element rendered in ReportModal
 * @param elementId - ID of the legends element
 * @param onProgress - Optional progress callback
 * @param retries - Number of retries if validation fails
 * @returns Base64 encoded PNG image
 * @throws ImageCaptureError if capture fails after retries
 */
export async function captureLegends(
  elementId: string = "legends-panel-for-capture",
  onProgress?: ProgressCallback,
  retries: number = 2
): Promise<string> {
  try {
    onProgress?.({
      step: "capturing-legends",
      message: "Capturando legendas...",
      percentage: 60,
    });

    ErrorLogger.logInfo(
      `Attempting to capture legends from element: ${elementId}`
    );

    // Get the hidden legends element (always rendered in ReportModal)
    const legendsElement = document.getElementById(elementId);

    if (!legendsElement) {
      const errorMessage = `Legends element with ID '${elementId}' not found`;
      ErrorLogger.logError(new Error(errorMessage), "captureLegends");
      throw new ImageCaptureError(
        "Elemento de legendas não encontrado. Por favor, recarregue a página.",
        "legends"
      );
    }

    if (!(legendsElement instanceof HTMLElement)) {
      ErrorLogger.logError(
        new Error("Legends element is not an HTMLElement"),
        "captureLegends"
      );
      throw new ImageCaptureError(
        "Elemento de legendas inválido. Por favor, recarregue a página.",
        "legends"
      );
    }

    ErrorLogger.logInfo("Legends element found, waiting for DOM readiness...");

    // Wait for DOM readiness (ResizeObserver stability)
    const readiness = await waitForLegendReadiness(legendsElement, 5000);
    if (readiness.ready) {
      ErrorLogger.logInfo(`Legend panel ready after ${readiness.waitTime}ms`);
    } else {
      ErrorLogger.logWarning(
        `Readiness check: ${readiness.reason}, proceeding anyway`
      );
    }

    // Wait for all images to load (custom icons)
    ErrorLogger.logInfo("Waiting for legend images to load...");
    await waitForImagesLoaded(legendsElement, 3000);

    // Extra RAF for paint cycle
    await waitForPaintCycle();

    ErrorLogger.logInfo("Starting legends capture...");

    // Capture the legends panel
    const dataUrl = await domToPng(legendsElement, {
      scale: 2,
      backgroundColor: "#ffffff",
      style: {
        visibility: "visible",
      },
    });

    ErrorLogger.logInfo(
      `Legends captured, size: ${(dataUrl.length / 1024).toFixed(2)} KB`
    );

    // Validate capture has proper content
    ErrorLogger.logInfo("Validating legend capture...");
    const validation = await validateCapture(dataUrl, {
      minWidth: 300,
      minHeight: 200,
      minColors: 5,
    });

    if (!validation.isValid && retries > 0) {
      ErrorLogger.logWarning(
        `Validation failed: ${validation.issues.join(
          ", "
        )}. Retrying... (${retries} attempts left)`
      );
      // Wait a bit before retry
      await new Promise((resolve) => setTimeout(resolve, 500));
      return captureLegends(elementId, onProgress, retries - 1);
    }

    if (!validation.isValid) {
      const errorMessage = `Failed to capture legends after retries: ${validation.issues.join(
        ", "
      )}`;
      ErrorLogger.logError(new Error(errorMessage), "captureLegends");
      throw new ImageCaptureError(
        "Falha ao capturar legendas. Tente novamente.",
        "legends"
      );
    }

    ErrorLogger.logInfo(
      `Capture validated successfully: ${validation.dimensions.width}x${validation.dimensions.height}, ${validation.colorVariety} colors`
    );

    // Compress the image
    try {
      const compressed = await compressImage(
        dataUrl,
        PDF_CONSTANTS.COMPRESSION_QUALITY,
        PDF_CONSTANTS.COMPRESSION_MAX_WIDTH,
        PDF_CONSTANTS.COMPRESSION_MAX_HEIGHT
      );
      ErrorLogger.logInfo(
        `Legends compressed, size: ${(compressed.length / 1024).toFixed(2)} KB`
      );
      return compressed;
    } catch (compressionError) {
      ErrorLogger.logWarning("Legend image compression failed, using original");
      return dataUrl;
    }
  } catch (error) {
    ErrorLogger.logError(
      error instanceof Error ? error : new Error(String(error)),
      "captureLegends"
    );
    throw new ImageCaptureError(
      "Falha ao capturar legendas. Verifique se há camadas ativas e tente novamente.",
      "legends"
    );
  }
}

/**
 * Fetch legend images from GeoServer URLs
 * @param legendUrls - Array of legend URLs
 * @param onProgress - Optional progress callback
 * @returns Array of legend URLs (backend will fetch them to avoid CORS)
 */
export async function fetchLegendImages(
  legendUrls: string[],
  onProgress?: ProgressCallback
): Promise<string[]> {
  onProgress?.({
    step: "fetching-legends",
    message: "Processando legendas...",
    percentage: 60,
  });

  // Pass URLs directly - backend will fetch them to avoid CORS issues
  return legendUrls;
}

/**
 * Collect metadata from the application state
 * @param projectName - Project name
 * @param description - Project description
 * @param location - Optional location data
 * @returns PDF metadata object
 */
export function collectMetadata(
  projectName: string,
  description: string,
  location?: { lat: number; lng: number; address?: string }
): PDFMetadata {
  return {
    projectName: projectName || "Relatório Sem Título",
    description: description || "",
    timestamp: new Date().toISOString(),
    location,
  };
}

/**
 * Validate PDF payload size
 * @param payload - PDF payload
 * @param maxSizeMB - Maximum size in megabytes
 * @returns True if valid, false otherwise
 */
export function validatePDFPayloadSize(
  payload: PDFPayload,
  maxSizeMB: number = 50
): boolean {
  const jsonString = JSON.stringify(payload);
  const sizeInMB = new Blob([jsonString]).size / (1024 * 1024);

  if (sizeInMB > maxSizeMB) {
    ErrorLogger.logError(
      new Error(
        `Payload size (${sizeInMB.toFixed(
          2
        )}MB) exceeds maximum (${maxSizeMB}MB)`
      ),
      "validatePayloadSize"
    );
    return false;
  }

  ErrorLogger.logInfo(`Payload size: ${sizeInMB.toFixed(2)}MB`);
  return true;
}
